// src/components/viewer/ViewerFreeTextSearch.tsx

import React, { useEffect, useRef, useState } from "react";
import {
  Group,
  TextInput,
  ActionIcon,
  Tooltip,
  Autocomplete,
} from "@mantine/core";
import { FaSearch } from "react-icons/fa";
import { FiX, FiInfo } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearRoomSearchInRedux,
  clearComponentSearchInRedux,
  selectComponentSearchText,
  selectRoomSearchText,
  setActiveSearchInRedux,
  setComponentSearchTextInRedux,
  setRoomSearchTextInRedux,
} from "../../ViewerRedux/viewerSlice";
import { selectComponentAutocompleteValues } from "../../ViewerRedux/viewerSelectors";
import { selectDuplicateSpaces } from "../../McBim/spaceDuplicateSelectors";

export interface ViewerFreeTextSearchProps {
  mode: "ROOM" | "COMPONENT";
  width?: number;
  disabled?: boolean;
  debounceMs?: number;
}

export const ViewerFreeTextSearch: React.FC<ViewerFreeTextSearchProps> = ({
  mode,
  width = 200,
  disabled = false,
  debounceMs = 150,
}) => {
  const dispatch = useAppDispatch();

  const roomText = useAppSelector(selectRoomSearchText);
  const componentText = useAppSelector(selectComponentSearchText);
  const componentValues = useAppSelector(selectComponentAutocompleteValues);
  const duplicateSpaces = useAppSelector(selectDuplicateSpaces);

  const isComponentMode = mode === "COMPONENT";
  const reduxValue = isComponentMode ? componentText : roomText;

  const [localValue, setLocalValue] = useState<string>(reduxValue);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setLocalValue(reduxValue);
  }, [reduxValue]);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  const dispatchValueDebounced = (next: string) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);

    timerRef.current = window.setTimeout(() => {
      if (isComponentMode) dispatch(setComponentSearchTextInRedux(next));
      else dispatch(setRoomSearchTextInRedux(next));
    }, debounceMs);
  };

  const onChangeValue = (next: string) => {
    dispatch(setActiveSearchInRedux(mode));
    setLocalValue(next);
    dispatchValueDebounced(next);
  };

  const handleClear = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setLocalValue("");
    dispatch(isComponentMode ? clearComponentSearchInRedux() : clearRoomSearchInRedux());
  };

  const helpText = isComponentMode
    ? "Søk på navn, komponentkode, IFC-klasse, typenavn eller system. Alle søkeord må matche."
    : "Søk på romnummer eller romnavn.";

  const rightSection = (
    <Group gap={2} wrap="nowrap">
      <Tooltip label={helpText} withArrow>
        <ActionIcon variant="subtle" size="sm" radius="xl">
          <FiInfo size={14} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label="Tøm søk" withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          radius="xl"
          disabled={!localValue.trim()}
          aria-label={isComponentMode ? "Tøm komponentsøk" : "Tøm romsøk"}
          onClick={handleClear}
        >
          <FiX size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  return (
    <Group gap="xs" align="center" wrap="nowrap">

      {isComponentMode ? (
        <Autocomplete
          style={{ width }}
          size="sm"
          placeholder="Søk komponent"
          aria-label="Søk komponent"
          disabled={disabled}
          onFocus={() => dispatch(setActiveSearchInRedux(mode))}
          value={localValue}
          onChange={onChangeValue}
          data={componentValues}
          limit={25}
          leftSection={<FaSearch size={12} />}
          rightSectionWidth={72}
          rightSection={rightSection}
          onOptionSubmit={(val) => {
            onChangeValue(val);
          }}
        />
      ) : (
        <TextInput
          disabled={disabled || duplicateSpaces.length > 0}
          style={{ width }}
          size="sm"
          placeholder="Søk rom"
          aria-label="Søk rom"
          onFocus={() => dispatch(setActiveSearchInRedux(mode))}
          value={localValue}
          onChange={(e) => onChangeValue(e.currentTarget.value)}
          leftSection={<FaSearch size={12} />}
          rightSectionWidth={72}
          rightSection={rightSection}
        />
      )}
    </Group>
  );
};
